import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatMoney } from '@/lib/money'

import { AdminHeader } from '../AdminLayout'
import { CollectionEditor, FormActions } from '../components/CollectionEditor'
import {
  I18nField,
  MoneyField,
  NumberField,
  SelectField,
  SwitchField,
  TextField,
  type I18nValue,
} from '../components/fields'
import { MediaField } from '../components/MediaField'
import { useAdminContext } from '../session'
import { useCollection } from '../useCollection'
import type {
  FoodPack,
  I18n,
  PricingRule,
  PricingRuleKind,
  PricingScope,
  TicketType,
} from '../types'

/** Les portées et les natures de règle, telles que le serveur les accepte. */
const SCOPES: PricingScope[] = [
  'order',
  'ticket',
  'food_item',
  'food_pack',
  'drink_item',
  'drink_table',
]

const KINDS: PricingRuleKind[] = [
  'discount_percent',
  'discount_amount',
  'surcharge_percent',
  'surcharge_amount',
  'price_override',
]

/** `datetime-local` veut `AAAA-MM-JJTHH:MM`, à l'heure locale ; l'API parle ISO. */
function toLocalInput(value: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  const pad = (part: number) => String(part).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

function fromLocalInput(value: string): string | null {
  return value ? new Date(value).toISOString() : null
}

/** Une règle en pourcentage porte `value_numeric` ; les autres, `value_minor`. */

const PERCENT_KINDS = new Set<PricingRuleKind>(['discount_percent', 'surcharge_percent'])

/**
 * Billets et tarification.
 *
 * Deux niveaux, volontairement séparés :
 *
 * * **le prix affiché** d'un type de billet — ce que voit le visiteur ;
 * * **les règles** qui le modifient — remises, suppléments, prix imposés.
 *
 * Les mêler dans un seul écran donnerait l'illusion qu'une remise « appartient »
 * à un billet. Elle n'appartient à rien : elle s'applique à une portée, dans un
 * ordre, sous conditions. Le total, lui, reste calculé par le serveur — cet
 * écran ne fait que décrire les règles du calcul.
 */
export function AdminTicketsPage() {
  const { t, i18n } = useTranslation()
  const { eventId, event } = useAdminContext()
  const currency = event?.currency ?? 'EUR'

  useEffect(() => {
    document.title = t('admin.section.tickets')
  }, [t])

  const tickets = useCollection<TicketType>({
    key: 'admin-ticket-types',
    path: `/admin/events/${eventId}/ticket-types`,
    eventId,
    related: [['ticket-types'], ['admin-readiness', eventId ?? '']],
  })
  const packs = useCollection<FoodPack>({
    key: 'admin-food-packs',
    path: `/admin/events/${eventId}/food/packs`,
    eventId,
  })
  const rules = useCollection<PricingRule>({
    key: 'admin-pricing-rules',
    path: `/admin/events/${eventId}/pricing/rules`,
    eventId,
  })

  function label(value: I18n): string {
    return value[i18n.language] || value[Object.keys(value)[0] ?? ''] || '—'
  }

  function price(minor: number): string {
    return formatMoney({ amount_minor: minor, currency }, i18n.language)
  }

  return (
    <>
      <AdminHeader
        title={t('admin.section.tickets')}
        description={t('admin.tickets.description')}
      />

      <div className="grid gap-6">
        <CollectionEditor<TicketType>
          title={t('admin.tickets.types')}
          description={t('admin.tickets.typesHint')}
          items={tickets.items}
          busy={tickets.busy}
          error={tickets.error}
          emptyLabel={t('admin.tickets.noTypes')}
          createLabel={t('admin.tickets.addType')}
          onDelete={(ticket) => tickets.remove.mutate({ id: ticket.id })}
          // Un type déjà vendu ne se supprime pas : les billets émis le
          // référencent. Le désactiver le retire de la vente sans rien casser.
          canDelete={(ticket) => ticket.sold_count === 0}
          summarize={(ticket) => ({
            title: label(ticket.name_i18n),
            subtitle: [
              ticket.code,
              t('admin.tickets.admits', { count: ticket.admits_count }),
              ticket.capacity === null
                ? t('admin.tickets.unlimited')
                : t('admin.tickets.sold', { sold: ticket.sold_count, total: ticket.capacity }),
            ].join(' · '),
            value: price(ticket.price_minor),
            dimmed: !ticket.is_active,
            badges: [
              ...(ticket.is_active ? [] : [{ label: t('admin.inactive') } as const]),
              ...(ticket.capacity !== null && ticket.sold_count >= ticket.capacity
                ? [{ label: t('admin.tickets.soldOut'), tone: 'danger' } as const]
                : []),
              ...(ticket.includes_food_pack_id
                ? [{ label: t('admin.tickets.withPack'), tone: 'success' } as const]
                : []),
            ],
          })}
          renderForm={(ticket, close) => (
            <TicketForm
              ticket={ticket}
              packs={packs.items}
              packLabel={label}
              currency={currency}
              eventId={eventId}
              busy={tickets.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (ticket) tickets.update.mutate({ id: ticket.id, ...payload }, done)
                else tickets.create.mutate(payload, done)
              }}
            />
          )}
        />

        <CollectionEditor<PricingRule>
          title={t('admin.pricing.rules')}
          description={t('admin.pricing.rulesHint')}
          items={rules.items}
          busy={rules.busy}
          error={rules.error}
          emptyLabel={t('admin.pricing.noRules')}
          createLabel={t('admin.pricing.addRule')}
          onDelete={(rule) => rules.remove.mutate({ id: rule.id })}
          summarize={(rule) => ({
            title: rule.name,
            subtitle: [
              t(`admin.pricing.scope.${rule.scope}`),
              t(`admin.pricing.kind.${rule.kind}`),
              rule.promo_code ? `« ${rule.promo_code} »` : null,
              t('admin.pricing.priority', { value: rule.priority }),
            ]
              .filter(Boolean)
              .join(' · '),
            value: PERCENT_KINDS.has(rule.kind)
              ? `${Number(rule.value_numeric ?? 0)} %`
              : price(rule.value_minor ?? 0),
            dimmed: !rule.is_active,
            badges: [
              ...(rule.stackable
                ? [{ label: t('admin.pricing.stackable') } as const]
                : [{ label: t('admin.pricing.exclusive'), tone: 'warning' } as const]),
              ...(rule.max_redemptions
                ? [
                    {
                      label: `${rule.redemption_count}/${rule.max_redemptions}`,
                    } as const,
                  ]
                : []),
            ],
          })}
          renderForm={(rule, close) => (
            <RuleForm
              rule={rule}
              currency={currency}
              busy={rules.busy}
              onCancel={close}
              onSubmit={(payload) => {
                const done = { onSuccess: () => close() }
                if (rule) rules.update.mutate({ id: rule.id, ...payload }, done)
                else rules.create.mutate(payload, done)
              }}
            />
          )}
        />
      </div>
    </>
  )
}

// ── Formulaires ────────────────────────────────────────────────────

function TicketForm({
  ticket,
  packs,
  packLabel,
  currency,
  eventId,
  busy,
  onSubmit,
  onCancel,
}: {
  ticket: TicketType | null
  packs: FoodPack[]
  packLabel: (value: I18n) => string
  currency: string
  eventId: string | undefined
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [code, setCode] = useState(ticket?.code ?? '')
  const [name, setName] = useState<I18nValue>(ticket?.name_i18n ?? {})
  const [description, setDescription] = useState<I18nValue>(ticket?.description_i18n ?? {})
  const [priceMinor, setPriceMinor] = useState<number | null>(ticket?.price_minor ?? null)
  const [admits, setAdmits] = useState<number | null>(ticket?.admits_count ?? 1)
  const [capacity, setCapacity] = useState<number | null>(ticket?.capacity ?? null)
  const [maxPerOrder, setMaxPerOrder] = useState<number | null>(ticket?.max_per_order ?? null)
  const [packId, setPackId] = useState(ticket?.includes_food_pack_id ?? '')
  const [active, setActive] = useState(ticket?.is_active ?? true)
  // La fenêtre de vente, la photo et les avantages existaient côté serveur
  // sans écran pour les régler : un tarif « early bird » ne pouvait pas
  // fermer seul à sa date.
  const [saleStart, setSaleStart] = useState(toLocalInput(ticket?.sale_start ?? null))
  const [saleEnd, setSaleEnd] = useState(toLocalInput(ticket?.sale_end ?? null))
  const [mediaId, setMediaId] = useState<string | null>(ticket?.media_id ?? null)
  // Une ligne = un avantage, par langue. Le serveur stocke une liste.
  const [benefits, setBenefits] = useState<I18nValue>(() =>
    Object.fromEntries(
      Object.entries(ticket?.benefits_i18n ?? {}).map(([locale, lines]) => [
        locale,
        lines.join('\n'),
      ]),
    ),
  )
  const windowInvalid =
    Boolean(saleStart && saleEnd) && new Date(saleEnd) <= new Date(saleStart)

  // Le code identifie le type dans les exports et les liens : il se fige à la
  // création. Le serveur ne l'accepte d'ailleurs pas en modification.
  const codeIsFixed = ticket !== null

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        {codeIsFixed ? null : (
          <TextField
            label={t('admin.tickets.code')}
            hint={t('admin.tickets.codeHint')}
            value={code}
            onChange={(value) => setCode(value.toUpperCase())}
          />
        )}
        <I18nField label={t('admin.food.name')} value={name} onChange={setName} />
        <I18nField
          label={t('admin.food.descriptionField')}
          value={description}
          onChange={setDescription}
        />
        <MoneyField
          label={t('admin.food.price')}
          minor={priceMinor}
          currency={currency}
          onChange={setPriceMinor}
        />
        <NumberField
          label={t('admin.tickets.admitsCount')}
          hint={t('admin.tickets.admitsHint')}
          value={admits}
          min={1}
          max={20}
          onChange={setAdmits}
        />
        <NumberField
          label={t('admin.tickets.capacity')}
          hint={t('admin.tickets.capacityHint')}
          value={capacity}
          min={0}
          onChange={setCapacity}
        />
        <NumberField
          label={t('admin.food.maxPerOrder')}
          hint={t('admin.food.maxPerOrderHint')}
          value={maxPerOrder}
          min={1}
          max={50}
          onChange={setMaxPerOrder}
        />
        <SelectField
          label={t('admin.tickets.includesPack')}
          hint={t('admin.tickets.includesPackHint')}
          value={packId}
          onChange={setPackId}
          options={[
            { value: '', label: t('admin.tickets.noPack') },
            ...packs.map((pack) => ({ value: pack.id, label: packLabel(pack.name_i18n) })),
          ]}
        />
        <SwitchField
          label={t('admin.active')}
          hint={t('admin.activeHint')}
          value={active}
          onChange={setActive}
        />
        <TextField
          type="datetime-local"
          label={t('admin.tickets.saleStart')}
          hint={t('admin.tickets.saleStartHint')}
          value={saleStart}
          onChange={setSaleStart}
        />
        <TextField
          type="datetime-local"
          label={t('admin.tickets.saleEnd')}
          hint={t('admin.tickets.saleEndHint')}
          value={saleEnd}
          onChange={setSaleEnd}
        />
        <div className="sm:col-span-2">
          <I18nField
            label={t('admin.tickets.benefits')}
            hint={t('admin.tickets.benefitsHint')}
            value={benefits}
            onChange={setBenefits}
            multiline
          />
        </div>
        <div className="sm:col-span-2">
          <MediaField
            label={t('admin.media.photo')}
            value={mediaId}
            eventId={eventId}
            onChange={setMediaId}
          />
        </div>
      </div>

      {windowInvalid ? (
        <p className="mt-3 text-xs text-state-warning">{t('admin.tickets.saleWindowInvalid')}</p>
      ) : null}

      {ticket && ticket.sold_count > 0 ? (
        <p className="mt-4 text-xs text-content-muted">
          {t('admin.tickets.alreadySold', { count: ticket.sold_count })}
        </p>
      ) : null}

      <FormActions
        busy={busy || windowInvalid || (!codeIsFixed && !/^[A-Z][A-Z0-9_]*$/.test(code))}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            ...(codeIsFixed ? {} : { code }),
            name_i18n: name,
            description_i18n: description,
            benefits_i18n: Object.fromEntries(
              Object.entries(benefits)
                .map(
                  ([locale, text]): [string, string[]] => [
                    locale,
                    text
                      .split('\n')
                      .map((line) => line.trim())
                      .filter(Boolean),
                  ],
                )
                .filter(([, lines]) => lines.length > 0),
            ),

            media_id: mediaId,
            price_minor: priceMinor ?? 0,
            admits_count: admits ?? 1,
            capacity,
            max_per_order: maxPerOrder,
            sale_start: fromLocalInput(saleStart),
            sale_end: fromLocalInput(saleEnd),
            includes_food_pack_id: packId || null,
            is_active: active,
          })
        }
      />
    </div>
  )
}

function RuleForm({
  rule,
  currency,
  busy,
  onSubmit,
  onCancel,
}: {
  rule: PricingRule | null
  currency: string
  busy: boolean
  onSubmit: (payload: Record<string, unknown>) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const [name, setName] = useState(rule?.name ?? '')
  const [scope, setScope] = useState<PricingScope>(rule?.scope ?? 'order')
  const [kind, setKind] = useState<PricingRuleKind>(rule?.kind ?? 'discount_percent')
  const [percent, setPercent] = useState<number | null>(
    rule?.value_numeric === null || rule?.value_numeric === undefined
      ? null
      : Number(rule.value_numeric),
  )
  const [amountMinor, setAmountMinor] = useState<number | null>(rule?.value_minor ?? null)
  const [promoCode, setPromoCode] = useState(rule?.promo_code ?? '')
  const [maxRedemptions, setMaxRedemptions] = useState<number | null>(
    rule?.max_redemptions ?? null,
  )
  const [priority, setPriority] = useState<number | null>(rule?.priority ?? 100)
  const [stackable, setStackable] = useState(rule?.stackable ?? false)
  const [active, setActive] = useState(rule?.is_active ?? true)

  const isPercent = PERCENT_KINDS.has(kind)

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2">
        <TextField label={t('admin.pricing.name')} value={name} onChange={setName} />
        <SelectField<PricingScope>
          label={t('admin.pricing.scopeLabel')}
          hint={t('admin.pricing.scopeHint')}
          value={scope}
          onChange={setScope}
          options={SCOPES.map((value) => ({
            value,
            label: t(`admin.pricing.scope.${value}`),
          }))}
        />
        <SelectField<PricingRuleKind>
          label={t('admin.pricing.kindLabel')}
          value={kind}
          onChange={setKind}
          options={KINDS.map((value) => ({ value, label: t(`admin.pricing.kind.${value}`) }))}
        />

        {/* Le champ de valeur suit la nature de la règle : un pourcentage
            n'est pas un montant, et les envoyer dans le mauvais champ fait
            échouer la validation côté serveur. */}
        {isPercent ? (
          <NumberField
            label={t('admin.pricing.percent')}
            value={percent}
            min={0}
            max={100}
            onChange={setPercent}
          />
        ) : (
          <MoneyField
            label={t('admin.pricing.amount')}
            minor={amountMinor}
            currency={currency}
            onChange={setAmountMinor}
          />
        )}

        <TextField
          label={t('admin.pricing.promoCode')}
          hint={t('admin.pricing.promoCodeHint')}
          value={promoCode}
          onChange={(value) => setPromoCode(value.toUpperCase())}
        />
        <NumberField
          label={t('admin.pricing.maxRedemptions')}
          hint={t('admin.pricing.maxRedemptionsHint')}
          value={maxRedemptions}
          min={1}
          onChange={setMaxRedemptions}
        />
        <NumberField
          label={t('admin.pricing.priorityLabel')}
          hint={t('admin.pricing.priorityHint')}
          value={priority}
          min={0}
          max={1000}
          onChange={setPriority}
        />
        <SwitchField
          label={t('admin.pricing.stackableLabel')}
          hint={t('admin.pricing.stackableHint')}
          value={stackable}
          onChange={setStackable}
        />
        <SwitchField
          label={t('admin.active')}
          hint={t('admin.activeHint')}
          value={active}
          onChange={setActive}
        />
      </div>

      {rule && rule.redemption_count > 0 ? (
        <p className="mt-4 text-xs text-content-muted">
          {t('admin.pricing.alreadyUsed', { count: rule.redemption_count })}
        </p>
      ) : null}

      <FormActions
        busy={busy || !name.trim() || (isPercent ? percent === null : amountMinor === null)}
        onCancel={onCancel}
        onSubmit={() =>
          onSubmit({
            name: name.trim(),
            scope,
            kind,
            // Les deux champs partent toujours : celui qui ne s'applique pas
            // vaut `null`, ce que le serveur exige plutôt qu'une valeur restée
            // d'un précédent choix de nature.
            value_numeric: isPercent ? percent : null,
            value_minor: isPercent ? null : amountMinor,
            promo_code: promoCode.trim() || null,
            max_redemptions: maxRedemptions,
            priority: priority ?? 100,
            stackable,
            is_active: active,
          })
        }
      />
    </div>
  )
}
